import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import statementBalanceEmailTemplateDeleteApi from 'models/core/statementBalanceEmailTemplate/delete/Api.StatementBalanceEmailTemplate.Delete';
import StatementBalanceEmailTemplate from 'models/core/statementBalanceEmailTemplate/Model.StatementBalanceEmailTemplate';
import statementBalanceEmailTemplateSaveApi from 'models/core/statementBalanceEmailTemplate/save/Api.StatementBalanceEmailTemplate.Save';
import { useMemo } from 'react';
import { statementBalanceEmailTemplateIndexRoute } from './StatementEmailTemplate.Index';

export enum StatementBalanceEmailTriggers {
  SendEmail = 'ANYSTATUS_SEND_EMAIL',
}

export enum StatementBalanceEmailTemplateAction {
  EDIT = 'edit',
  DELETE = 'delete',
}
export type VerifyStatementBalanceEmailTemplateAction = Utils.VerifyExtends<
  Module.Sales.StatementBalanceEmailTemplate.Actions,
  Utils.ValueOf<typeof StatementBalanceEmailTemplateAction>
>;
export default function useStatementBalanceEmailTemplate() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.Sales.StatementBalanceEmailTemplate.Params,
    Module.Sales.StatementBalanceEmailTemplate.Query
  >({
    route: statementBalanceEmailTemplateIndexRoute,
  });

  const canView =
    permissions.CREATE_EMAILTEMPLATE || permissions.EDIT_EMAILTEMPLATE || permissions.DELETE_EMAILTEMPLATE;
  const templateApi = useApi({ action: StatementBalanceEmailTemplate.list });

  const listApi = useApi({
    action: StatementBalanceEmailTemplate.list,
    default: { limit: defaultLimit, offset: defaultOffset },
    body: pageRouter.query.list,
    callback: (req) => {
      templateApi.execute((b) => b);
      pageRouter.redirect({}, { ...pageRouter.query, list: req });
    },
    wait: !canView,
  });

  const deleteApi = useApi({
    action: statementBalanceEmailTemplateDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const saveApi = useApi({
    action: statementBalanceEmailTemplateSaveApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const availableStatuses = useMemo(() => {
    const statementStatuses = Object.values({ ...StatementBalanceEmailTriggers })?.map((status) => status);
    const usedStatuses = templateApi?.payload?.data?.map((it) => it.statementBalanceStatusTrigger) || [];

    return [
      ...statementStatuses.filter((status: any) => !usedStatuses.includes(status)),
      ...[selected?.statementBalanceStatusTrigger],
    ].filter(Boolean);
  }, [templateApi?.payload?.data, selected]);

  return {
    canView,
    pageRouter,
    listApi,
    saveApi,
    deleteApi,
    selected,
    permissions,
    availableStatuses,
    views: {
      [StatementBalanceEmailTemplateAction.DELETE]: !!(
        pageRouter.params.action === StatementBalanceEmailTemplateAction.DELETE &&
        selected &&
        permissions.DELETE_EMAILTEMPLATE
      ),
      [StatementBalanceEmailTemplateAction.EDIT]: !!(
        pageRouter.params.action === StatementBalanceEmailTemplateAction.EDIT &&
        ((permissions.CREATE_EMAILTEMPLATE && !selected) || (permissions.EDIT_EMAILTEMPLATE && selected))
      ),
    },
  };
}

export function useStatementBalanceEmailTemplateProvider() {
  return useDataProvider<ReturnType<typeof useStatementBalanceEmailTemplate>>();
}
